import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import styled from '@mui/material/styles/styled';
import { Plus } from '../../assets/images';
import PageDots from '../../components/pageDots';
import BottomGradient from '../../components/bottomGradient';
import { PaperCard } from '../../components/card/PaperCard';
import ChallengeItem, { DailyCountChallengeItem } from '../../components/challengeItem';
import Recommendation from '../../components/recommendation';
import {
    UserInfoCard,
    RankingCard,
    WalkingDataCard,
    HeartRateDataCard,
    DidimActivityCard,
    StressIndexCard,
    ChampionshipCard,
} from './components/cards';
import {
    formatDateTime,
    isAdmin,
    isSupervisor,
    getLocalizedName,
} from '../../utils/base';
import { CHAMPIONSHIP_TYPES, API_PAGE_SIZE } from '../../constants';
import { logAPI, membershipAPI, physicalRecordAPI, challengeAPI, championAPI, contentAPI, recommendationAPI, userAPI, skillAPI, eventAPI } from '../../actions/imsServer';
import { useNavigate, useLocation } from 'react-router';
import Popup from '../../components/popup';
import { getTodayStartTime, isWebView } from '../../utils/base';
import { isDailyStepRewardReceivedToday, getLocalDateKey } from '../../utils/stepRewardStatus';
import { useInterval } from '../../actions/interval';

// 마운트 시 fetch 중복 호출 방지 (여러 effect/리렌더에서 동시에 호출되는 경우)
let fetchUserDataInProgress = false;

// Styled components
const DashboardContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    position: 'relative',
    zIndex: 1,
    overflow: 'auto',
    paddingBottom: '200px',
    minHeight: '100vh',
    transition: 'opacity 0.3s ease-in-out',

}));

export const UserMain = () => {
    const { t, i18n } = useTranslation();
    const tRef = useRef(t);
    tRef.current = t;
    const meInfoIdRef = useRef(null);
    const isFetchingRef = useRef(false);
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');
    meInfoIdRef.current = meInfo?.id ?? null;

    // 실제 데이터 상태
    const [userData, setUserData] = useState(null);
    const [physicalRecordLatest, setPhysicalRecordLatest] = useState(null);
    const [physicalRecordToday, setPhysicalRecordToday] = useState(null);
    const [yesterdayPhysicalRecord, setYesterdayPhysicalRecord] = useState(null);
    const [membershipsData, setMembershipsData] = useState(null);
    const [challengeProgressData, setChallengeProgressData] = useState(null);
    const [allChallenges, setAllChallenges] = useState([]);
    const [playLogsData, setPlayLogsData] = useState(null);
    const [recommendation, setRecommendation] = useState(null);
    const [skillData, setSkillData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [popupSubMessage, setPopupSubMessage] = useState('');
    const [sortedMemberships, setSortedMemberships] = useState(null);

    const [currentStepChallengePage, setCurrentStepChallengePage] = useState(0);
    const [currentHeartRateChallengePage, setCurrentHeartRateChallengePage] = useState(0);
    const [currentDidimChallengePage, setCurrentDidimChallengePage] = useState(0);
    const [currentRankingPage, setCurrentRankingPage] = useState(0);
    const [currentPageEvent, setCurrentPageEvent] = useState(0);

    // 걷기 챌린지 페이징 로직
    const filteredStepChallenges = useMemo(() => {
        const progressChallenges = challengeProgressData?.filter(item => item.challenge.type === 0) || [];
        const allStepChallenges = allChallenges.filter(challenge => challenge.type === 0);

        // 진행도가 있는 챌린지의 ID 목록
        const progressIds = new Set(progressChallenges.map(item => item.challenge.id));

        // 진행도가 없는 챌린지들을 빈 카드로 추가
        const emptyChallenges = allStepChallenges
            .filter(challenge => !progressIds.has(challenge.id))
            .map(challenge => ({
                challenge: challenge,
                value: 0,
                completedCount: 0,
                completedDates: []
            }));

        // id 순으로 정렬
        const allChallengesCombined = [...progressChallenges, ...emptyChallenges];
        return allChallengesCombined.sort((a, b) => {
            const aId = a.challenge?.id || 0;
            const bId = b.challenge?.id || 0;
            return aId - bId;
        });
    }, [challengeProgressData, allChallenges]);
    const totalStepChallengePages = filteredStepChallenges.length;

    const handleStepChallengePage = (page) => {
        setCurrentStepChallengePage(page);
    };

    // 심박수 챌린지 페이징 로직
    const filteredHeartRateChallenges = useMemo(() => {
        const progressChallenges = challengeProgressData?.filter(item => item.challenge?.type === 1) || [];
        const allHeartRateChallenges = allChallenges.filter(challenge => challenge.type === 1);

        // 진행도가 있는 챌린지의 ID 목록
        const progressIds = new Set(progressChallenges.map(item => item.challenge.id));

        // 진행도가 없는 챌린지들을 빈 카드로 추가
        const emptyChallenges = allHeartRateChallenges
            .filter(challenge => !progressIds.has(challenge.id))
            .map(challenge => ({
                challenge: challenge,
                value: 0,
                completedCount: 0,
                completedDates: []
            }));

        // id 순으로 정렬
        const allChallengesCombined = [...progressChallenges, ...emptyChallenges];
        return allChallengesCombined.sort((a, b) => {
            const aId = a.challenge?.id || 0;
            const bId = b.challenge?.id || 0;
            return aId - bId;
        });
    }, [challengeProgressData, allChallenges]);
    const totalHeartRateChallengePages = filteredHeartRateChallenges.length;

    const handleHeartRateChallengePage = (page) => {
        setCurrentHeartRateChallengePage(page);
    };

    // 디딤 챌린지 페이징 로직
    const filteredDidimChallenges = useMemo(() => {
        const progressChallenges = challengeProgressData?.filter(item => item.challenge?.type === 2 || item.challenge?.type === 3) || [];
        const allDidimChallenges = allChallenges.filter(challenge => challenge.type === 2 || challenge.type === 3);

        // 진행도가 있는 챌린지의 ID 목록
        const progressIds = new Set(progressChallenges.map(item => item.challenge.id));

        // 진행도가 없는 챌린지들을 빈 카드로 추가
        const emptyChallenges = allDidimChallenges
            .filter(challenge => !progressIds.has(challenge.id))
            .map(challenge => ({
                challenge: challenge,
                value: 0,
                completedCount: 0,
                completedDates: []
            }));

        // id 순으로 정렬
        const allChallengesCombined = [...progressChallenges, ...emptyChallenges];
        return allChallengesCombined.sort((a, b) => {
            const aId = a.challenge?.id || 0;
            const bId = b.challenge?.id || 0;
            return aId - bId;
        });
    }, [challengeProgressData, allChallenges]);
    const totalDidimChallengePages = filteredDidimChallenges.length;

    const handleDidimChallengePage = (page) => {
        setCurrentDidimChallengePage(page);
    };

    // 랭킹 페이징 로직
    const memberShips = sortedMemberships?.memberships || [];
    const totalRankingCards = memberShips.length; // 실제 랭킹 카드 수
    const totalRankingPages = totalRankingCards + 1; // 랭킹 카드 + 기관 추가 카드   
    const [currentOrganizationId, setcurrentOrganizationId] = useState(null);
    const [currentOrganizationName, setcurrentOrganizationName] = useState(null);

    // 스와이프 관련 상태 (랭킹 카드용)
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    // 각 카드별 스와이프 상태
    const [stepChallengeTouchStart, setStepChallengeTouchStart] = useState(null);
    const [stepChallengeTouchEnd, setStepChallengeTouchEnd] = useState(null);
    const [heartRateChallengeTouchStart, setHeartRateChallengeTouchStart] = useState(null);
    const [heartRateChallengeTouchEnd, setHeartRateChallengeTouchEnd] = useState(null);
    const [didimChallengeTouchStart, setDidimChallengeTouchStart] = useState(null);
    const [didimChallengeTouchEnd, setDidimChallengeTouchEnd] = useState(null);
    const [eventTouchStart, setEventTouchStart] = useState(null);
    const [eventTouchEnd, setEventTouchEnd] = useState(null);

    // 최소 스와이프 거리 (px)
    const minSwipeDistance = 50;

    const handleRankingPage = (page) => {
        setCurrentRankingPage(page);
    };

    // 공통 스와이프 핸들러 생성 함수
    const createSwipeHandlers = (touchStartState, setTouchStartState, touchEndState, setTouchEndState, currentPage, totalPages, onPageChange) => {
        // ref를 사용하여 최신 state 값을 참조
        const touchStartRef = { current: touchStartState };
        const touchEndRef = { current: touchEndState };

        return {
            onTouchStart: (e) => {
                const startX = e.targetTouches[0].clientX;
                touchStartRef.current = startX;
                touchEndRef.current = null;
                setTouchEndState(null);
                setTouchStartState(startX);
            },
            onTouchMove: (e) => {
                const endX = e.targetTouches[0].clientX;
                touchEndRef.current = endX;
                setTouchEndState(endX);
            },
            onTouchEnd: () => {
                const start = touchStartRef.current;
                const end = touchEndRef.current;

                if (!start || !end) {
                    return;
                }

                const distance = start - end;
                const isLeftSwipe = distance > minSwipeDistance;
                const isRightSwipe = distance < -minSwipeDistance;

                if (isLeftSwipe && currentPage < totalPages - 1) {
                    onPageChange(currentPage + 1);
                } else if (isRightSwipe && currentPage > 0) {
                    onPageChange(currentPage - 1);
                }
            }
        };
    };

    // 랭킹 카드 스와이프 핸들러
    const rankingSwipeHandlers = createSwipeHandlers(
        touchStart, setTouchStart, touchEnd, setTouchEnd,
        currentRankingPage, totalRankingPages, handleRankingPage
    );

    // Step Challenge 스와이프 핸들러
    const stepChallengeSwipeHandlers = createSwipeHandlers(
        stepChallengeTouchStart, setStepChallengeTouchStart,
        stepChallengeTouchEnd, setStepChallengeTouchEnd,
        currentStepChallengePage, totalStepChallengePages, handleStepChallengePage
    );

    // Heart Rate Challenge 스와이프 핸들러
    const heartRateChallengeSwipeHandlers = createSwipeHandlers(
        heartRateChallengeTouchStart, setHeartRateChallengeTouchStart,
        heartRateChallengeTouchEnd, setHeartRateChallengeTouchEnd,
        currentHeartRateChallengePage, totalHeartRateChallengePages, handleHeartRateChallengePage
    );

    // Didim Challenge 스와이프 핸들러
    const didimChallengeSwipeHandlers = createSwipeHandlers(
        didimChallengeTouchStart, setDidimChallengeTouchStart,
        didimChallengeTouchEnd, setDidimChallengeTouchEnd,
        currentDidimChallengePage, totalDidimChallengePages, handleDidimChallengePage
    );

    // Championship 스와이프 핸들러는 championshipData와 currentPageChampionship 선언 이후에 생성됨

    // 랭킹 페이지 변경 시 currentOrganizationId 업데이트
    useEffect(() => {
        const memberShips = sortedMemberships?.memberships || [];
        const currentRanking = memberShips[currentRankingPage];

        if (currentRanking) {
            const orgId = currentRanking.organizationId;
            setcurrentOrganizationId(orgId);
            setcurrentOrganizationName(currentRanking.organization?.name);
        }
    }, [currentRankingPage, sortedMemberships]);

    // 실제 데이터 가져오기 (t는 ref로 참조해 의존성에서 제외 → 무한 로딩 방지)
    const fetchUserData = useCallback(async () => {
        if (isFetchingRef.current || fetchUserDataInProgress) return;
        isFetchingRef.current = true;
        fetchUserDataInProgress = true;
        const t = tRef.current;
        try {
            // SNS 로그인 직후: localStorage에 meInfo가 있으면 즉시 표시해 "로딩 중..." 해제
            let currentUserInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');
            if (currentUserInfo?.id) {
                setUserData(JSON.stringify(currentUserInfo));
                setLoading(false);
            } else {
                setLoading(true);
            }
            setError(null);

            try {
                const userResponse = await userAPI.getMyInfo();

                if (userResponse?.data?.user) {
                    const userData = userResponse.data.user;
                    // localStorage 업데이트
                    localStorage.setItem('meInfo', JSON.stringify(userData));
                    setUserData(JSON.stringify(userData));
                    currentUserInfo = userData;
                } else {
                    // API 응답이 없으면 localStorage에서 가져오기
                    const storedInfo = localStorage.getItem('meInfo');
                    if (storedInfo) {
                        currentUserInfo = JSON.parse(storedInfo);
                    }
                    setUserData(storedInfo);
                }
            } catch (userInfoError) {
                console.error('사용자 정보 가져오기 실패:', userInfoError);
                // API 호출 실패 시 localStorage에서 가져오기
                const storedInfo = localStorage.getItem('meInfo');
                if (storedInfo) {
                    currentUserInfo = JSON.parse(storedInfo);
                }
                setUserData(storedInfo);
            }

            // 사용자 ID가 없으면 종료
            if (!currentUserInfo?.id) {
                console.error('사용자 ID를 가져올 수 없습니다.');
                setError('failed_to_load_user_info');
                setLoading(false);
                return;
            }

            // 오늘의 신체 기록 가져오기
            try {
                const physicalResponse = await physicalRecordAPI.getPhysicalRecords({
                    userId: currentUserInfo.id,
                    state: 0,
                    limit: 1
                });

                if (physicalResponse?.data?.physicalRecords?.length > 0) {
                    // 가장 최근 기록만 사용              
                    setPhysicalRecordLatest(physicalResponse.data.physicalRecords[0]);
                    setPhysicalRecordToday(physicalResponse.data.physicalRecords.find(record => formatDateTime(record.lastDate, '/', ':', 0, 10) === formatDateTime(new Date(), '/', ':', 0, 10)));
                }
            } catch (physicalError) {
                console.error('신체 기록 가져오기 실패:', physicalError);
                // 에러가 발생해도 계속 진행
            }

            // 어제 날짜의 physicalRecord 가져오기
            try {
                // 로컬 시간대의 어제 날짜 계산
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                const year = yesterday.getFullYear();
                const month = String(yesterday.getMonth() + 1).padStart(2, '0');
                const day = String(yesterday.getDate()).padStart(2, '0');
                const yesterdayStr = `${year}-${month}-${day}`;

                const yesterdayResponse = await physicalRecordAPI.getPhysicalRecords({
                    userId: currentUserInfo.id,
                    state: 0,
                    startDate: yesterdayStr,
                    endDate: yesterdayStr,
                    limit: 1
                });

                if (yesterdayResponse?.data?.physicalRecords && yesterdayResponse.data.physicalRecords.length > 0) {
                    setYesterdayPhysicalRecord(yesterdayResponse.data.physicalRecords[0]);
                } else {
                    setYesterdayPhysicalRecord(null);
                }
            } catch (err) {
                console.error('어제 physicalRecord 로딩 실패:', err);
                setYesterdayPhysicalRecord(null);
            }

            // 오늘의 디딤 사용량 가져오기
            const todayStartTime = new Date();
            todayStartTime.setHours(0, 0, 0, 0);

            // 기관 정보 가져오기
            let membershipsResponse = null;
            try {
                membershipsResponse = await membershipAPI.getMemberships({
                    userId: currentUserInfo.id,
                    type: 1,
                    state: 0,
                    expand: 'organization'
                });

                if (membershipsResponse?.data) {
                    setMembershipsData(membershipsResponse.data);
                }
            } catch (membershipsError) {
                console.error('기관 정보 가져오기 실패:', membershipsError);
                // 에러가 발생해도 계속 진행
            }

            // 전체 챌린지 목록 가져오기
            let challengeProgressResponse = null;
            let challengesResponse = null;
            try {
                const results = await Promise.allSettled([
                    (async () => {
                        let offset = 0;
                        let allProgresses = [];
                        let page;
                        do {
                            const res = await challengeAPI.getChallengeProgress({
                                userId: currentUserInfo.id,
                                state: 0,
                                expand: 'challenge',
                                offset
                            });
                            page = res?.data?.challengeProgresses || [];
                            allProgresses = allProgresses.concat(page);
                            offset += API_PAGE_SIZE;
                        } while (page.length === API_PAGE_SIZE);
                        return { data: { challengeProgresses: allProgresses } };
                    })(),
                    (async () => {
                        let offset = 0;
                        let allChallenges = [];
                        let page;
                        do {
                            const res = await challengeAPI.getChallenges({
                                state: 0,
                                offset
                            });
                            page = res?.data?.challenges || [];
                            allChallenges = allChallenges.concat(page);
                            offset += API_PAGE_SIZE;
                        } while (page.length === API_PAGE_SIZE);
                        return { data: { challenges: allChallenges } };
                    })()
                ]);

                if (results[0].status === 'fulfilled') {
                    challengeProgressResponse = results[0].value;
                    if (challengeProgressResponse?.data) {
                        const challengeProgresses = challengeProgressResponse.data.challengeProgresses;
                        setChallengeProgressData(challengeProgresses);

                        if (isWebView()) {
                            // 걷기 일간 챌린지(physical_step_daily)만 성공 시 앱에 전달
                            const dailyStepRewardReceived = isDailyStepRewardReceivedToday(
                                challengeProgresses
                            );
                            // 보상 수령했을 때만 앱에 전달 (미수령·다음날은 앱 dateKey로 푸시 허용)
                            if (dailyStepRewardReceived) {
                                window.ReactNativeWebView.postMessage(
                                    JSON.stringify({
                                        type: 'stepRewardStatus',
                                        challengeName: 'physical_step_daily',
                                        data: {
                                            received: true,
                                            dateKey: getLocalDateKey(),
                                        },
                                    })
                                );
                            }
                        }
                    }
                } else {
                    console.error('챌린지 진행도 가져오기 실패:', results[0].reason);
                }

                if (results[1].status === 'fulfilled') {
                    challengesResponse = results[1].value;
                    if (challengesResponse?.data?.challenges) {
                        setAllChallenges(challengesResponse.data.challenges);
                    }
                } else {
                    console.error('챌린지 목록 가져오기 실패:', results[1].reason);
                }
            } catch (challengeError) {
                console.error('챌린지 데이터 가져오기 실패:', challengeError);
                // 에러가 발생해도 계속 진행
            }

            // 사용자가 가입한 기관 ID 목록 추출
            const userOrganizationIds = [];
            if (membershipsResponse?.data?.memberships) {
                membershipsResponse.data.memberships.forEach(membership => {
                    if (membership.organizationId) {
                        userOrganizationIds.push(String(membership.organizationId));
                    }
                });
            }

            // organizationId에 가입한 기관 ID들과 0을 콤마로 연결
            const championParams = { state: '0,2', expand: 'organization,ranker' };
            if (userOrganizationIds.length > 0) {
                championParams.organizationId = `${userOrganizationIds.join(',')},0`;
            } else {
                championParams.organizationId = '0';
            }

            // 여러 API를 병렬로 호출하되, 하나가 실패해도 다른 것들은 계속 진행
            let playLogsResponse = null;
            let championsResponse = null;
            let contentsResponse = null;
            let recommendationResponse = null;
            let skillResponse = null;
            let eventsResponse = null;

            try {
                const results = await Promise.allSettled([
                    (async () => {
                        let offset = 0;
                        let allLogs = [];
                        let page;
                        do {
                            const res = await logAPI.getPlayLogs({
                                userId: currentUserInfo.id,
                                startTime: getTodayStartTime(),
                                expand: 'organization',
                                offset
                            });
                            page = res?.data?.logs ?? [];
                            allLogs = allLogs.concat(page);
                            offset += API_PAGE_SIZE;
                        } while (page.length === API_PAGE_SIZE);
                        return { data: { logs: allLogs } };
                    })(),
                    (async () => {
                        let offset = 0;
                        let allChampions = [];
                        let page;
                        do {
                            const res = await championAPI.getChampions({
                                ...championParams,
                                offset
                            });
                            page = res?.data?.champions ?? [];
                            allChampions = allChampions.concat(page);
                            offset += API_PAGE_SIZE;
                        } while (page.length === API_PAGE_SIZE);
                        return { data: { champions: allChampions } };
                    })(),
                    (async () => {
                        let offset = 0;
                        let allContents = [];
                        let page;
                        do {
                            const res = await contentAPI.getContents({
                                state: 0,
                                offset
                            });
                            page = res?.data?.contents ?? [];
                            allContents = allContents.concat(page);
                            offset += API_PAGE_SIZE;
                        } while (page.length === API_PAGE_SIZE);
                        return { data: { contents: allContents } };
                    })(),
                    recommendationAPI.getRecommendations({ userId: currentUserInfo.id }),
                    skillAPI.getSkills({ userId: currentUserInfo.id }),
                    eventAPI.getEvents({
                        status: 'ongoing', // 진행중인 이벤트만 가져오기
                    })
                ]);

                // 각 결과 처리
                if (results[0].status === 'fulfilled') {
                    playLogsResponse = results[0].value;
                } else {
                    console.error('플레이 로그 가져오기 실패:', results[0].reason);
                }

                if (results[1].status === 'fulfilled') {
                    championsResponse = results[1].value;
                } else {
                    console.error('챔피언십 가져오기 실패:', results[1].reason);
                }

                if (results[2].status === 'fulfilled') {
                    contentsResponse = results[2].value;
                } else {
                    console.error('콘텐츠 가져오기 실패:', results[2].reason);
                }

                if (results[3].status === 'fulfilled') {
                    recommendationResponse = results[3].value;
                } else {
                    console.error('추천 가져오기 실패:', results[3].reason);
                }

                if (results[4].status === 'fulfilled') {
                    skillResponse = results[4].value;
                } else {
                    console.error('스킬 가져오기 실패:', results[4].reason);
                }

                if (results[5].status === 'fulfilled') {
                    eventsResponse = results[5].value;
                } else {
                    console.error('이벤트 가져오기 실패:', results[5].reason);
                }
            } catch (apiError) {
                console.error('API 호출 중 오류 발생:', apiError);
                // 에러가 발생해도 계속 진행
            }

            if (isWebView() && playLogsResponse?.data?.logs) {
                const mainOrgLogs = playLogsResponse.data.logs.filter(
                    log => log.organizationId === currentUserInfo.mainOrganizationId
                );
                const stepCountSum = mainOrgLogs.reduce(
                    (acc, curr) => acc + (curr.stepCount || 0), 0
                );
                const durationSumSec = mainOrgLogs.reduce((acc, curr) => {
                    const start = curr.startTime ? new Date(curr.startTime).getTime() : 0;
                    const end = curr.endTime ? new Date(curr.endTime).getTime() : 0;
                    return acc + Math.max(0, end - start) / 1000;
                }, 0);
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: 'playData',
                        data: {
                            didim: stepCountSum,
                            mins: (durationSumSec / 60).toFixed(2),
                            updateTime: new Date().getTime()
                        },
                    })
                );
            }

            if (playLogsResponse?.data.logs) {
                setPlayLogsData(playLogsResponse.data.logs);
            }

            // 챔피언십 데이터 처리
            const champions = championsResponse?.data?.champions || [];
            const contents = contentsResponse?.data?.contents || [];

            const contentMap = new Map(contents.map(c => [c.index, c]));

            // API에서 이미 필터링된 데이터이므로 추가 필터링 불필요
            const processedChampionships = champions.map(champion => {
                const content = contentMap.get(champion.contentIndex) || {};

                // 대회주최자 이름 결정
                // userGrade가 슈퍼바이저(80 이상)이면 '디딤 본사'
                // userGrade가 어드민(50-60)이면 organization.name
                // userGrade가 유저(그 외)이면 userName
                let hostName = champion.userName || '-';
                const userGrade = champion?.userGrade || 0;

                if (isSupervisor(userGrade)) {
                    hostName = t('didim_hq');
                } else if (isAdmin(userGrade)) {
                    hostName = champion.organization?.name || '-';
                }

                // 진행 상태 확인: state 기준으로 진행중/완료 구분 (내부 키 사용: ongoing/complete)
                // state: 0 → 진행중, state: 2 → 완료
                const state = champion.state !== undefined ? champion.state : 0;
                const progress = state === 2 ? 'complete' : 'ongoing';

                // 보상 정보 파싱 (info 필드에서 추출)
                const rewardInfo = champion.info || '';
                const rewardMatch = rewardInfo.match(/(\d+등)\s*(.+)/);
                const rank = rewardMatch ? rewardMatch[1] : t('1st');
                const reward = rewardMatch ? rewardMatch[2].trim() : rewardInfo || '-';

                // 안전하게 문자열로 변환하는 헬퍼 함수들
                const extractString = (value, defaultValue = '-') => {
                    if (value === null || value === undefined) return defaultValue;
                    if (typeof value === 'string') return value.trim() || defaultValue;
                    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
                    if (typeof value === 'object') {
                        // 객체인 경우 내부 필드에서 문자열 추출
                        if (value.name && typeof value.name === 'string' && value.name.trim()) {
                            return value.name.trim();
                        }
                        if (value.nickname && typeof value.nickname === 'string' && value.nickname.trim()) {
                            return value.nickname.trim();
                        }
                        if (value.profilePath && typeof value.profilePath === 'string' && value.profilePath.trim()) {
                            return value.profilePath.trim();
                        }
                        return defaultValue;
                    }
                    try {
                        return String(value).trim() || defaultValue;
                    } catch (e) {
                        return defaultValue;
                    }
                };

                return {
                    id: champion.id,
                    name: extractString(champion.name, '-'),
                    type: champion.type,
                    organizer: hostName,
                    startDate: champion.startDate,
                    endDate: champion.endDate,
                    reward: reward,
                    rank: rank,
                    contentName: getLocalizedName(content, i18n.language) || content.name,
                    icon: content.icon,
                    participants: t('count', { count: champion.playerCount || 0 }),
                    currentRank: progress === 'complete' ? t('user_main_progress_complete') : t('user_main_progress_ongoing'),
                    currentWinner: extractString(champion.ranker, ''),
                    currentScore: extractString(champion.ranker, ''),
                    isLocked: champion.password && champion.password.length > 0,
                    method: CHAMPIONSHIP_TYPES.find(type => type.value === champion.type)?.label || '',
                    userId: champion.userId, // 내가 만든 챔피언십인지 확인하기 위해 추가
                    progress: progress, // 진행 상태 저장
                    organizationId: champion.organizationId, // 기관 ID 추가
                };
            });

            // 진행중인 챔피언십만 필터링
            const ongoingChampionships = processedChampionships.filter(championship => championship.progress === 'ongoing');

            // 정렬: 내가 만든 챔피언십 먼저, 그 다음 endDate 빠른 순
            const sortedChampionships = ongoingChampionships.sort((a, b) => {
                const aIsMine = a.userId === currentUserInfo.id;
                const bIsMine = b.userId === currentUserInfo.id;

                // 내가 만든 챔피언십이 먼저
                if (aIsMine && !bIsMine) return -1;
                if (!aIsMine && bIsMine) return 1;

                // 같은 그룹 내에서는 endDate 빠른 순
                const aEndDate = a.endDate ? new Date(a.endDate).getTime() : Infinity;
                const bEndDate = b.endDate ? new Date(b.endDate).getTime() : Infinity;
                return aEndDate - bEndDate;
            });

            // 총 10개로 제한
            const limitedChampionships = sortedChampionships.slice(0, 10);

            setChampionshipData(limitedChampionships);

            // AI 추천 데이터 처리
            const fitnessData = recommendationResponse?.data?.recommendations?.[0]?.fitness;
            if (fitnessData && Array.isArray(fitnessData)) {
                // contentIndex로 getContents 호출
                const contentMap = new Map(contents.map(c => [c.index, c]));

                // content name을 현재 언어로 표시
                const getContentNameSafe = (content) => {
                    if (!content) return t('exercise');
                    const name = getLocalizedName(content, i18n.language);
                    if (name && typeof name === 'string' && name.trim()) return name.trim();
                    return t('exercise');
                };

                const processedRecommendation = fitnessData.map(item => {
                    const content = contentMap.get(item.contentIndex);
                    return {
                        contentIndex: item.contentIndex,
                        name: getContentNameSafe(content),
                        icon: content?.icon || null,
                        circuit: item.circuit || '',
                        comment: item.comment || '',
                    };
                });
                setRecommendation(processedRecommendation);

            } else if (fitnessData) {
                // 기존 형식 (하위 호환성)
                setRecommendation(fitnessData);
            }

            // 체력 지표 데이터 저장
            if (skillResponse?.data?.skills) {
                setSkillData(skillResponse.data.skills);
            } else {
                setSkillData(null);
            }

            // 이벤트 데이터 처리 (서버에서 이미 필터링됨)
            const events = eventsResponse?.data?.events || eventsResponse?.events || [];
            const total = eventsResponse?.data?.total || eventsResponse?.total || 0;

            // 이벤트 데이터만 간단하게 처리 (이미지만 표시)
            const processedEvents = events.map((event) => {
                // 이미지 URL 확인 (여러 필드명 시도)
                const imageUrl = event.file || event.imageUrl || event.image || null;

                return {
                    id: event.id,
                    name: event.name || '',
                    info: event.info || '',
                    startDate: event.startDate || '',
                    endDate: event.endDate || '',
                    rewardName: event.rewardName || '',
                    expirePeriod: event.expirePeriod || 0,
                    expireValue: event.expireValue || 0,
                    sendDate: event.sendDate || '',
                    file: imageUrl,
                };
            });

            setEventData(processedEvents);
            setEventOffset(processedEvents.length);
            eventOffsetRef.current = processedEvents.length;
            setEventHasMore(processedEvents.length < total && processedEvents.length === API_PAGE_SIZE);

        } catch (err) {
            console.error('데이터 로딩 오류:', err);
            setError('failed_to_load_user_info');
        } finally {
            isFetchingRef.current = false;
            fetchUserDataInProgress = false;
            setLoading(false);
        }
    }, []);

    // 앱 재실행 시: localStorage에 meInfo가 있으면 즉시 표시 (로딩 중... 멈춤 방지)
    useEffect(() => {
        try {
            const s = localStorage.getItem('meInfo');
            const stored = s ? JSON.parse(s) : null;
            if (stored?.id) {
                setUserData(JSON.stringify(stored));
                setLoading(false);
            }
        } catch (_) {}
    }, []);

    // 컴포넌트 마운트 시 1회만 fetch
    const hasInitialFetchedRef = useRef(false);
    useEffect(() => {
        if (hasInitialFetchedRef.current) return;
        hasInitialFetchedRef.current = true;
        fetchUserData();
    }, [fetchUserData]);

    // meInfoChanged 이벤트 리스닝 (카카오 로그인 등으로 meInfo가 변경될 때)
    useEffect(() => {
        const handleMeInfoChanged = () => {
            // localStorage에서 최신 meInfo 가져오기
            const latestMeInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');
            if (latestMeInfo?.id) {
                // 사용자 정보가 있으면 데이터 다시 가져오기
                fetchUserData();
            }
        };

        window.addEventListener('meInfoChanged', handleMeInfoChanged);

        return () => {
            window.removeEventListener('meInfoChanged', handleMeInfoChanged);
        };
    }, [fetchUserData]); // fetchUserData를 의존성에 추가

    // 카카오 로그인 후 localStorage 변경 감지 (마운트 후 1초부터 5초간만, 500ms 간격) - ref로 최신 id 비교
    useEffect(() => {
        let checkCount = 0;
        const maxChecks = 10;
        let timeoutId = null;

        const checkMeInfo = () => {
            checkCount++;
            const storedMeInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');
            const currentMeInfoId = meInfoIdRef.current;

            if (storedMeInfo?.id && storedMeInfo.id !== currentMeInfoId) {
                fetchUserData();
                return;
            }

            if (checkCount < maxChecks) {
                timeoutId = setTimeout(checkMeInfo, 500);
            }
        };

        // 초기 fetch 완료 후에만 폴링 시작 (중복 호출 방지)
        const startId = setTimeout(() => checkMeInfo(), 1000);

        return () => {
            clearTimeout(startId);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [fetchUserData]);

    // 10분마다 자동 리로드 (useInterval 사용)
    useInterval(() => {
        if (meInfo.id) {
            fetchUserData();
        }
    }, meInfo.id ? 10 * 60 * 1000 : null); // 10분 = 600,000ms

    // 회원가입 후 face 페이지로 리다이렉트
    useEffect(() => {
        if (location.state?.redirectToFace) {
            // state를 제거하고 face로 이동 (replace: true로 히스토리에 남기지 않음)
            navigate('/users/face', {
                replace: true,
                state: { fromSignUp: true }
            });
        }
    }, [location.state, navigate]);

    // memberships를 mainOrganizationId 우선으로 정렬
    useEffect(() => {
        if (membershipsData?.memberships?.length > 0) {
            // 최신 사용자 정보 가져오기 (userDataState 또는 localStorage에서)
            let mainOrgId = null;
            if (userData) {
                try {
                    const parsedUserData = JSON.parse(userData);
                    mainOrgId = parsedUserData.mainOrganizationId || parsedUserData.mainorganizationid;
                } catch (e) {
                    // 파싱 실패 시 localStorage에서 직접 가져오기
                    try {
                        const storedInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');
                        mainOrgId = storedInfo.mainOrganizationId || storedInfo.mainorganizationid;
                    } catch (e2) {
                        mainOrgId = meInfo.mainOrganizationId || meInfo.mainorganizationid;
                    }
                }
            } else {
                mainOrgId = meInfo.mainOrganizationId || meInfo.mainorganizationid;
            }

            const sorted = [...membershipsData.memberships].sort((a, b) => {
                const aIsMain = a.organizationId === mainOrgId;
                const bIsMain = b.organizationId === mainOrgId;
                if (aIsMain && !bIsMain) return -1;
                if (!aIsMain && bIsMain) return 1;
                return 0;
            });
            setSortedMemberships({ ...membershipsData, memberships: sorted });
        } else {
            setSortedMemberships(null);
        }
    }, [membershipsData, userData, meInfo?.mainOrganizationId]);

    // 초기 기관 ID 설정
    useEffect(() => {
        if (sortedMemberships?.memberships?.length > 0 && !currentOrganizationId) {
            const firstOrg = sortedMemberships.memberships[0];
            setcurrentOrganizationId(firstOrg?.organizationId);
            setcurrentOrganizationName(firstOrg?.organization?.name);
        }
    }, [sortedMemberships, currentOrganizationId]);

    // 메인기관 변경 후 새로고침 함수
    const handleMainOrganizationChange = async () => {
        // 랭킹 페이지를 1페이지(인덱스 0)로 이동
        setCurrentRankingPage(0);
        // 데이터 새로고침
        await fetchUserData();
    };

    // 팝업 표시 함수
    const showPopup = (message, subMessage = '') => {
        setPopupMessage(message);
        setPopupSubMessage(subMessage);
        setPopupOpen(true);
    };

    // 팝업 닫기 함수
    const handleClosePopup = () => {
        setPopupOpen(false);
    };

    const processedUserData = userData ? {
        ...JSON.parse(userData || '{}'),
        events: [
            {
                myEntries: 23, // 실제 데이터가 없으므로 기본값
            },
        ],
    } : null;


    const [championshipData, setChampionshipData] = useState([]);

    const [eventData, setEventData] = useState([]);
    const [, setEventOffset] = useState(0);
    const [eventHasMore, setEventHasMore] = useState(true);
    const [eventIsLoadingMore, setEventIsLoadingMore] = useState(false);
    const eventOffsetRef = useRef(0);

    const [currentPageChampionship, setCurrentPageChampionship] = useState(0);

    // 선택된 기관에 맞는 챔피언십 필터링 (선택된 기관 + 기관 ID 0)
    const filteredChampionshipData = useMemo(() => {
        if (!championshipData || championshipData.length === 0) {
            return [];
        }

        if (!currentOrganizationId) {
            // 기관이 선택되지 않았으면 기관 ID 0인 챔피언십만 표시
            return championshipData.filter(championship => {
                const orgId = championship.organizationId ?? championship.champion?.organizationId;
                return orgId === 0 || orgId === '0';
            });
        }

        // 선택된 기관의 챔피언십 + 기관 ID 0인 챔피언십
        return championshipData.filter(championship => {
            const orgId = championship.organizationId ?? championship.champion?.organizationId;
            const currentOrgIdStr = String(currentOrganizationId);
            const orgIdStr = String(orgId);
            return orgIdStr === currentOrgIdStr || orgId === 0 || orgId === '0';
        });
    }, [championshipData, currentOrganizationId]);

    // 기관 변경 시 챔피언십 페이지 초기화
    useEffect(() => {
        setCurrentPageChampionship(0);
    }, [currentOrganizationId]);

    // 선택된 기관에 맞는 체력 지표 데이터 필터링
    const filteredSkillData = useMemo(() => {
        if (!skillData) {
            return {
                endurance: 0,
                strength: 0,
                balance: 0,
                flexibility: 0,
                reaction: 0,
                cooperation: 0,
            };
        }

        // skillData가 배열인 경우
        const skills = Array.isArray(skillData) ? skillData : [skillData];

        // 선택된 기관에 맞는 skill 데이터 찾기
        const filteredSkill = skills.find(skill => {
            const skillOrgId = skill?.organizationId;
            if (!currentOrganizationId) {
                return skillOrgId === 0 || skillOrgId === '0' || !skillOrgId;
            }
            return String(skillOrgId) === String(currentOrganizationId);
        });

        return {
            endurance: filteredSkill?.endurance || 0,
            strength: filteredSkill?.strength || 0,
            balance: filteredSkill?.balance || 0,
            flexibility: filteredSkill?.flexibility || 0,
            reaction: filteredSkill?.reaction || 0,
            cooperation: filteredSkill?.cooperation || 0,
        };
    }, [skillData, currentOrganizationId]);

    // Championship 터치 위치 ref (각 카드별로 관리)
    const championshipTouchDataRef = useRef({});

    // 부모 Box 스와이프용 ref
    const championshipParentSwipeRef = useRef({
        startX: null,
        startY: null,
        endX: null,
        endY: null,
        isVerticalScroll: false
    });

    // Championship 스와이프 핸들러 (부모 Box용 - 카드 외부 영역 스와이프 처리)
    const championshipSwipeHandlers = {
        onTouchStart: (e) => {
            const touch = e.targetTouches[0];
            championshipParentSwipeRef.current.startX = touch.clientX;
            championshipParentSwipeRef.current.startY = touch.clientY;
            championshipParentSwipeRef.current.endX = null;
            championshipParentSwipeRef.current.endY = null;
            championshipParentSwipeRef.current.isVerticalScroll = false;
        },
        onTouchMove: (e) => {
            const touch = e.targetTouches[0];
            championshipParentSwipeRef.current.endX = touch.clientX;
            championshipParentSwipeRef.current.endY = touch.clientY;

            // 수직 이동이 수평 이동보다 크면 스크롤로 간주
            const deltaX = Math.abs(touch.clientX - championshipParentSwipeRef.current.startX);
            const deltaY = Math.abs(touch.clientY - championshipParentSwipeRef.current.startY);
            if (deltaY > deltaX) {
                championshipParentSwipeRef.current.isVerticalScroll = true;
            }
        },
        onTouchEnd: () => {
            const { startX, endX, isVerticalScroll } = championshipParentSwipeRef.current;

            if (!startX) return;

            // 수직 스크롤이 발생했으면 스와이프 무시
            if (isVerticalScroll) {
                championshipParentSwipeRef.current = { startX: null, endX: null, startY: null, endY: null, isVerticalScroll: false };
                return;
            }

            const finalEndX = endX !== null ? endX : startX;
            const isLeftSwipe = (startX - finalEndX) > minSwipeDistance;
            const isRightSwipe = (finalEndX - startX) > minSwipeDistance;

            if (isLeftSwipe && currentPageChampionship < filteredChampionshipData.length - 1) {
                setCurrentPageChampionship(currentPageChampionship + 1);
            } else if (isRightSwipe && currentPageChampionship > 0) {
                setCurrentPageChampionship(currentPageChampionship - 1);
            }

            // 리셋
            championshipParentSwipeRef.current = { startX: null, endX: null, startY: null, endY: null, isVerticalScroll: false };
        }
    };

    // 각 챔피언십 카드의 터치 핸들러 생성
    const createChampionshipCardHandlers = (championship) => {
        const cardId = championship.id;

        return {
            onTouchStart: (e) => {
                e.stopPropagation();
                const touch = e.targetTouches[0];
                championshipTouchDataRef.current[cardId] = {
                    startX: touch.clientX,
                    startY: touch.clientY,
                    endX: null,
                    endY: null,
                    isSwipe: false,
                    isVerticalScroll: false
                };
            },
            onTouchMove: (e) => {
                e.stopPropagation();
                const touch = e.targetTouches[0];
                if (championshipTouchDataRef.current[cardId]) {
                    const touchData = championshipTouchDataRef.current[cardId];
                    touchData.endX = touch.clientX;
                    touchData.endY = touch.clientY;

                    // 수직 이동이 수평 이동보다 크면 스크롤로 간주
                    const deltaX = Math.abs(touch.clientX - touchData.startX);
                    const deltaY = Math.abs(touch.clientY - touchData.startY);
                    if (deltaY > deltaX) {
                        touchData.isVerticalScroll = true;
                    }
                }
            },
            onTouchEnd: (e) => {
                e.stopPropagation();
                const touchData = championshipTouchDataRef.current[cardId];

                if (!touchData || !touchData.startX) {
                    return;
                }

                // 수직 스크롤이 발생했으면 스와이프 무시
                if (touchData.isVerticalScroll) {
                    delete championshipTouchDataRef.current[cardId];
                    return;
                }

                const startX = touchData.startX;
                const endX = touchData.endX !== null ? touchData.endX : startX;
                const isLeftSwipe = (startX - endX) > minSwipeDistance;
                const isRightSwipe = (endX - startX) > minSwipeDistance;

                // 스와이프 처리
                if (isLeftSwipe && currentPageChampionship < filteredChampionshipData.length - 1) {
                    touchData.isSwipe = true;
                    setCurrentPageChampionship(currentPageChampionship + 1);
                } else if (isRightSwipe && currentPageChampionship > 0) {
                    touchData.isSwipe = true;
                    setCurrentPageChampionship(currentPageChampionship - 1);
                } else {
                    // 클릭 처리
                    touchData.isSwipe = false;
                    setTimeout(() => {
                        // 스와이프가 발생하지 않았으면 클릭으로 처리
                        if (!championshipTouchDataRef.current[cardId]?.isSwipe) {
                            handleChampionshipClick(e, championship.id, championship);
                        }
                        // 정리
                        delete championshipTouchDataRef.current[cardId];
                    }, 100);
                    return;
                }

                // 정리
                delete championshipTouchDataRef.current[cardId];
            }
        };
    };

    const handleChampionshipClick = (e, championshipId, champion) => {
        if (e) {
            e.stopPropagation();
        }

        if (!championshipId) {
            return;
        }

        navigate('/championships/user-detail', {
            state: {
                championshipId: championshipId,
                championshipData: champion
            }
        });
    };

    // Event 스와이프 핸들러
    const handleEventPage = (page) => {
        setCurrentPageEvent(page);
    };

    const totalEventPages = eventData.length;
    const eventSwipeHandlers = createSwipeHandlers(
        eventTouchStart, setEventTouchStart,
        eventTouchEnd, setEventTouchEnd,
        currentPageEvent, totalEventPages, handleEventPage
    );

    // 이벤트 무한스크롤 데이터 가져오기
    const fetchMoreEvents = useCallback(async () => {
        if (eventIsLoadingMore || !eventHasMore) return;

        setEventIsLoadingMore(true);

        try {
            const eventsResponse = await eventAPI.getEvents({
                state: 0,
                offset: eventOffsetRef.current,
                status: 'ongoing', // 진행중인 이벤트만
            });

            const events = eventsResponse?.data?.events || [];
            const total = eventsResponse?.data?.total || 0;

            const processedEvents = events.map((event) => {
                const imageUrl = event.file || event.imageUrl || event.image || null;
                return {
                    id: event.id,
                    name: event.name || '',
                    info: event.info || '',
                    startDate: event.startDate || '',
                    endDate: event.endDate || '',
                    rewardName: event.rewardName || '',
                    expirePeriod: event.expirePeriod || 0,
                    expireValue: event.expireValue || 0,
                    sendDate: event.sendDate || '',
                    file: imageUrl,
                };
            });

            setEventData(prev => {
                const combined = [...prev, ...processedEvents];
                const unique = combined.filter(
                    (event, index, self) =>
                        index === self.findIndex(e => e.id === event.id)
                );
                return unique;
            });

            const newOffset = eventOffsetRef.current + processedEvents.length;
            setEventOffset(newOffset);
            eventOffsetRef.current = newOffset;
            setEventHasMore(newOffset < total && processedEvents.length === API_PAGE_SIZE);
        } catch (err) {
            console.error('이벤트 추가 로딩 실패:', err);
        } finally {
            setEventIsLoadingMore(false);
        }
    }, [eventIsLoadingMore, eventHasMore]);

    // 이벤트 스크롤 핸들러
    const handleEventScroll = useCallback((e) => {
        const target = e.target;
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;

        // 스크롤이 하단 근처(200px 이내)에 도달했을 때
        if (scrollHeight - scrollTop - clientHeight < 200 && eventHasMore && !eventIsLoadingMore && !loading) {
            fetchMoreEvents();
        }
    }, [eventHasMore, eventIsLoadingMore, loading, fetchMoreEvents]);


    return (
        <DashboardContainer
            onScroll={handleEventScroll}
            sx={{
                opacity: loading && !processedUserData ? 0.9 : 1,
                transition: 'opacity 0.2s ease-in-out',
            }}
        >
            <BottomGradient height="128px" width="100%" opacity={0.8} />

            {loading && !processedUserData ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '20px',
                        minHeight: '50vh',
                        backgroundColor: 'transparent',
                    }}
                >
                    <Typography>{t('loading')}</Typography>
                </Box>
            ) : error ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                    <Typography color={theme.palette.figma.white[60]}>{t('error')}: {t(error)}</Typography>
                </Box>
            ) : processedUserData && (
                <>
                    {/* User Info Card */}
                    <UserInfoCard
                        userData={processedUserData}
                    />
                    <Box
                        onTouchStart={rankingSwipeHandlers.onTouchStart}
                        onTouchMove={rankingSwipeHandlers.onTouchMove}
                        onTouchEnd={rankingSwipeHandlers.onTouchEnd}
                        sx={{
                            userSelect: 'none',
                            overflow: 'hidden',
                            width: '100%',
                            position: 'relative',
                            zIndex: 10,
                            minHeight: totalRankingCards === 0 ? '165px' : '400px', // 랭킹 카드가 없으면 165px, 있으면 400px
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                transform: currentRankingPage === 0 ? 'translateX(0px)' : `translateX(calc(-${currentRankingPage} * (100% + 20px)))`,
                                transition: 'transform 0.3s ease-out',
                                gap: '20px',
                                width: '100%',
                                height: '100%',
                            }}
                        >
                            {/* 모든 랭킹 카드 렌더링 */}
                            {memberShips && memberShips.length > 0 && memberShips.map((membership, index) => {
                                return (
                                    <Box
                                        key={membership.id || index}
                                        sx={{
                                            flexShrink: 0,
                                            width: '100%',
                                        }}
                                    >
                                        <RankingCard
                                            userData={processedUserData}
                                            membership={membership}
                                            onRefresh={handleMainOrganizationChange}
                                        />
                                    </Box>
                                );
                            })}

                            {/* 기관 추가 카드 */}
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    width: '100%',
                                }}
                            >
                                <PaperCard contentSx={{ padding: 0 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            width: '100%',
                                            height: totalRankingCards === 0 ? '165px' : '382px', // 랭킹 카드가 없으면 165px, 있으면 382px
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            gap: '10px',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 400,
                                                color: theme.palette.figma.white[60],
                                                letterSpacing: '-0.42px',
                                                textAlign: 'center',
                                            }}
                                        >
                                            {t('add_affiliated_organization')}
                                        </Typography>
                                        <Box
                                            sx={{
                                                width: '20px',
                                                height: '20px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                            onClick={() =>
                                                totalRankingPages === 6 ? showPopup(t('you_can_add_up_to_5_affiliated_organizations')) :
                                                    navigate('/auth/organization-search', {
                                                        state: { source: 'main' }
                                                    })
                                            }
                                        >
                                            <Plus size={20} />
                                        </Box>
                                    </Box>
                                </PaperCard>
                            </Box>
                        </Box>
                    </Box>

                    {/* Page Dots */}
                    {totalRankingCards > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <PageDots
                                totalPages={totalRankingCards}
                                currentPage={currentRankingPage < totalRankingCards ? currentRankingPage : totalRankingCards - 1}
                                onPageChange={handleRankingPage}
                            />
                        </Box>
                    )}

                    {/* Walking Data Card */}
                    <WalkingDataCard
                        physicalRecord={physicalRecordLatest}
                        onClick={() => navigate('/reports/activity')}
                    />

                    {/* Walking Challenge Card */}
                    {/* 챌린지 카드들 - 페이징 적용 */}
                    {filteredStepChallenges.length > 0 && (
                        <Box
                            onTouchStart={stepChallengeSwipeHandlers.onTouchStart}
                            onTouchMove={stepChallengeSwipeHandlers.onTouchMove}
                            onTouchEnd={stepChallengeSwipeHandlers.onTouchEnd}
                            sx={{
                                userSelect: 'none',
                                overflow: 'hidden',
                                width: '100%',
                                position: 'relative',
                                zIndex: 10,
                                minHeight: '206px',
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'stretch',
                                    transform: currentStepChallengePage === 0 ? 'translateX(0px)' : `translateX(calc(-${currentStepChallengePage} * (100% + 20px)))`,
                                    transition: 'transform 0.3s ease-out',
                                    gap: '20px',
                                    width: '100%',
                                }}
                            >
                                {filteredStepChallenges.map((challenge, index) => (
                                    <Box
                                        key={`step-challenge-${index}`}
                                        sx={{
                                            flexShrink: 0,
                                            width: '100%',
                                        }}
                                    >
                                        {((challenge.challenge.targetCount.length === 1 && challenge.challenge.targetCount[0] === 1) || challenge.challenge.targetCount.length > 1) ? (
                                            <ChallengeItem
                                                type={challenge.challenge.type}
                                                nameKey={challenge.challenge.name}
                                                value={challenge.value}
                                                completedRewardPoints={challenge?.rewardPoints}
                                                period={challenge?.challenge.period}
                                                targetValue={challenge?.challenge.targetValue[0]}
                                                rewardPoint={challenge?.challenge.rewardPoint}
                                                lastDate={challenge?.lastDate}
                                                onClick={() => navigate('/rewards/challenge')}
                                                user={processedUserData}
                                            />
                                        ) : (
                                            <DailyCountChallengeItem
                                                type={challenge?.challenge.type}
                                                period={challenge?.challenge.period}
                                                nameKey={challenge?.challenge.name}
                                                value={challenge?.value}
                                                completedCount={challenge?.completedCount}
                                                completedDates={challenge?.completedDates}
                                                targetCount={challenge?.challenge.targetCount}
                                                targetValue={challenge?.challenge.targetValue[0]}
                                                rewardPoint={challenge?.challenge.rewardPoint[0]}
                                                lastDate={challenge?.lastDate}
                                                onClick={() => navigate('/rewards/challenge')}
                                            />
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {filteredStepChallenges.length > 0 && <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <PageDots
                            totalPages={totalStepChallengePages}
                            currentPage={currentStepChallengePage}
                            onPageChange={handleStepChallengePage}
                        />
                    </Box>}

                    {/* Heart Rate Data Card */}
                    {meInfo?.hasWearable !== 0 && (
                        <HeartRateDataCard
                            physicalRecord={physicalRecordToday}
                            onClick={() => navigate('/reports/heartbeat')}
                        />
                    )}

                    {/* Heart Rate Challenge Card */}
                    {meInfo?.hasWearable !== 0 && filteredHeartRateChallenges.length > 0 && (
                        <Box
                            onTouchStart={heartRateChallengeSwipeHandlers.onTouchStart}
                            onTouchMove={heartRateChallengeSwipeHandlers.onTouchMove}
                            onTouchEnd={heartRateChallengeSwipeHandlers.onTouchEnd}
                            sx={{
                                userSelect: 'none',
                                overflow: 'hidden',
                                width: '100%',
                                position: 'relative',
                                zIndex: 10,
                                minHeight: '206px',
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'stretch',
                                    transform: currentHeartRateChallengePage === 0 ? 'translateX(0px)' : `translateX(calc(-${currentHeartRateChallengePage} * (100% + 20px)))`,
                                    transition: 'transform 0.3s ease-out',
                                    gap: '20px',
                                    width: '100%',
                                }}
                            >
                                {filteredHeartRateChallenges.map((challenge, index) => (
                                    <Box
                                        key={`heart-rate-challenge-${index}`}
                                        sx={{
                                            flexShrink: 0,
                                            width: '100%',
                                        }}
                                    >
                                        <ChallengeItem
                                            type={challenge.challenge.type}
                                            nameKey={challenge.challenge?.name}
                                            value={challenge.value}
                                            period={challenge?.challenge.period}
                                            targetValue={challenge?.challenge?.targetValue}
                                            rewardPoint={challenge?.challenge?.rewardPoint}
                                            lastDate={challenge?.lastDate}
                                            onClick={() => navigate('/rewards/challenge')}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {meInfo?.hasWearable !== 0 && filteredHeartRateChallenges.length > 0 && <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <PageDots
                            totalPages={totalHeartRateChallengePages}
                            currentPage={currentHeartRateChallengePage}
                            onPageChange={handleHeartRateChallengePage}
                        />
                    </Box>}

                    {/* Stress Index Card */}
                    {meInfo?.hasWearable !== 0 && (
                        <StressIndexCard
                            physicalRecord={physicalRecordToday}
                        />
                    )}

                    {/* Didim Data Card */}
                    {memberShips && memberShips.length > 0 && (
                        <DidimActivityCard
                            organizationName={currentOrganizationName}
                            playLogsData={currentOrganizationId ? playLogsData?.filter(log => log.organizationId === currentOrganizationId) : []}
                            skill={filteredSkillData}
                            onClick={() => navigate('/reports/didim')}
                        />
                    )}

                    {/* AI Exercise Recommendation Card */}
                    {recommendation && (
                        <Recommendation
                            data={recommendation}
                            userName={meInfo?.name || ''}
                            yesterdaySteps={yesterdayPhysicalRecord?.totalSteps || null}
                            avgHeartRate={yesterdayPhysicalRecord?.avgHeartRate || null}
                        />
                    )}

                    {/* Didim Challenge Card */}
                    {memberShips && memberShips.length > 0 && filteredDidimChallenges.length > 0 && (
                        <Box
                            onTouchStart={didimChallengeSwipeHandlers.onTouchStart}
                            onTouchMove={didimChallengeSwipeHandlers.onTouchMove}
                            onTouchEnd={didimChallengeSwipeHandlers.onTouchEnd}
                            sx={{
                                userSelect: 'none',
                                overflow: 'hidden',
                                width: '100%',
                                position: 'relative',
                                zIndex: 10,
                                minHeight: '206px',
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'stretch',
                                    transform: currentDidimChallengePage === 0 ? 'translateX(0px)' : `translateX(calc(-${currentDidimChallengePage} * (100% + 20px)))`,
                                    transition: 'transform 0.3s ease-out',
                                    gap: '20px',
                                    width: '100%',
                                }}
                            >
                                {filteredDidimChallenges.map((challenge, index) => (
                                    <Box
                                        key={`didim-challenge-${index}`}
                                        sx={{
                                            flexShrink: 0,
                                            width: '100%',
                                        }}
                                    >
                                        {((challenge.challenge.targetCount.length === 1 && challenge.challenge.targetCount[0] === 1) || challenge.challenge.targetCount.length > 1) ? (
                                            <ChallengeItem
                                                type={challenge.challenge.type}
                                                nameKey={challenge.challenge?.name}
                                                completedCount={challenge.completedCount}
                                                completedRewardPoints={challenge?.rewardPoints}
                                                value={challenge.value}
                                                period={challenge.challenge.period}
                                                targetCount={challenge?.challenge?.targetCount}
                                                targetValue={challenge?.challenge?.targetValue}
                                                rewardPoint={challenge?.challenge?.rewardPoint}
                                                lastDate={challenge?.lastDate}
                                                onClick={() => navigate('/rewards/challenge')}
                                            />
                                        ) : (
                                            <DailyCountChallengeItem
                                                type={challenge.challenge.type}
                                                nameKey={challenge.challenge?.name}
                                                completedCount={challenge.completedCount}
                                                completedDates={challenge.completedDates}
                                                value={challenge.value}
                                                period={challenge.challenge.period}
                                                targetCount={challenge?.challenge?.targetCount}
                                                targetValue={challenge?.challenge?.targetValue}
                                                rewardPoint={challenge?.challenge?.rewardPoint}
                                                lastDate={challenge?.lastDate}
                                                onClick={() => navigate('/rewards/challenge')}
                                            />
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {memberShips && memberShips.length > 0 && filteredDidimChallenges.length > 0 && <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <PageDots
                            totalPages={totalDidimChallengePages}
                            currentPage={currentDidimChallengePage}
                            onPageChange={handleDidimChallengePage}
                        />
                    </Box>}

                    {/* Championship Card */}
                    {memberShips && memberShips.length > 0 && filteredChampionshipData.length > 0 && (
                        <Box
                            onTouchStart={championshipSwipeHandlers.onTouchStart}
                            onTouchMove={championshipSwipeHandlers.onTouchMove}
                            onTouchEnd={championshipSwipeHandlers.onTouchEnd}
                            sx={{
                                userSelect: 'none',
                                overflow: 'hidden',
                                width: '100%',
                                position: 'relative',
                                zIndex: 10,
                                minHeight: '326px',
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    transform: currentPageChampionship === 0 ? 'translateX(0px)' : `translateX(calc(-${currentPageChampionship} * (100% + 20px)))`,
                                    transition: 'transform 0.3s ease-out',
                                    gap: '20px',
                                    width: '100%',
                                }}
                            >
                                {/* 필터링된 챔피언십 카드 렌더링 */}
                                {filteredChampionshipData.map((championship, index) => {
                                    const cardHandlers = createChampionshipCardHandlers(championship);

                                    return (
                                        <Box
                                            key={championship.id || index}
                                            sx={{
                                                flexShrink: 0,
                                                width: '100%',
                                            }}
                                        >
                                            <PaperCard
                                                background="#ffa100"
                                                sx={{
                                                    padding: 0,
                                                    justifyContent: 'center',
                                                    alignSelf: 'center',
                                                    width: '100%',
                                                }}
                                                contentSx={{
                                                    margin: 0,
                                                    padding: 0,
                                                    justifyContent: 'center',
                                                    width: '100%',
                                                }}
                                                onClick={(e) => {
                                                    // 마우스 클릭 처리 (터치 디바이스가 아닐 때만)
                                                    if (!('ontouchstart' in window)) {
                                                        handleChampionshipClick(e, championship.id, championship.champion);
                                                    }
                                                }}
                                                onTouchStart={cardHandlers.onTouchStart}
                                                onTouchMove={cardHandlers.onTouchMove}
                                                onTouchEnd={cardHandlers.onTouchEnd}
                                            >
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        height: '100%',
                                                    }}
                                                >
                                                    <ChampionshipCard
                                                        name={championship.name}
                                                        type={championship.type}
                                                        organizer={championship.organizer}
                                                        startDate={championship.startDate}
                                                        endDate={championship.endDate}
                                                        reward={championship.reward}
                                                        rank={championship.rank}
                                                        contentName={championship.contentName}
                                                        participants={championship.participants}
                                                        currentRank={championship.currentRank}
                                                        currentWinner={championship.currentWinner}
                                                        currentScore={championship.currentScore}
                                                    />
                                                </Box>
                                            </PaperCard>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}

                    {/* Page Dots */}
                    {memberShips && memberShips.length > 0 && filteredChampionshipData.length > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <PageDots
                                totalPages={filteredChampionshipData.length}
                                currentPage={currentPageChampionship}
                                onPageChange={setCurrentPageChampionship}
                            />
                        </Box>
                    )}

                    {/* Event Cards */}
                    {eventData.length > 0 && (
                        <Box
                            onTouchStart={eventSwipeHandlers.onTouchStart}
                            onTouchMove={eventSwipeHandlers.onTouchMove}
                            onTouchEnd={eventSwipeHandlers.onTouchEnd}
                            sx={{
                                userSelect: 'none',
                                overflow: 'hidden',
                                width: '100%',
                                position: 'relative',
                                zIndex: 10,
                                minHeight: '243px',
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    transform: currentPageEvent === 0 ? 'translateX(0px)' : `translateX(calc(-${currentPageEvent} * (100% + 20px)))`,
                                    transition: 'transform 0.3s ease-out',
                                    gap: '20px',
                                    width: '100%',
                                }}
                            >
                                {eventData.map((event, index) => {
                                    const imageUrl = event.file;
                                    return (
                                        <Box
                                            key={event.id || index}
                                            onClick={() => navigate('/events/user')}
                                            sx={{
                                                cursor: 'pointer',
                                                flexShrink: 0,
                                                width: '100%',
                                                borderRadius: '10px',
                                                overflow: 'hidden',
                                                minHeight: '243px',
                                            }}
                                        >
                                            {imageUrl ? (
                                                <Box
                                                    component="img"
                                                    src={imageUrl}
                                                    alt={event.name || t('event_image')}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                    }}
                                                    sx={{
                                                        width: '100%',
                                                        height: 'auto',
                                                        display: 'block',
                                                        objectFit: 'cover',
                                                    }}
                                                />
                                            ) : (
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        height: '200px',
                                                        backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                    }}
                                                >
                                                    <Typography>{t('no_image')}</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}

                    {/* Event Loading Indicator */}
                    {eventIsLoadingMore && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                            <Typography sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)' }}>
                                {t('loading')}
                            </Typography>
                        </Box>
                    )}

                    {/* Event Page Dots */}
                    {eventData.length > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <PageDots
                                totalPages={totalEventPages}
                                currentPage={currentPageEvent}
                                onPageChange={handleEventPage}
                            />
                        </Box>
                    )}
                </>
            )}
            <Popup
                open={popupOpen}
                message={popupMessage}
                subMessage={popupSubMessage}
                onConfirm={handleClosePopup}
            />
        </DashboardContainer>
    );
};
